import type firebase from "firebase/compat/app";
import type { DateValue } from "../types/data";
import { isTimestamp } from "../types/data";
import type { ActivityMetadata } from "../types/activity";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { FaListUl, FaTimes } from "react-icons/fa";
import { useFirebase } from "../context/FirebaseContext";
import { useI18n } from "../i18n/useI18n";
import { markNotificationsLogStale } from "../utility/activityFeedFreshness";
import { isDemoUserId } from "../utility/demoSession";

interface CollectionNotification {
  id: string;
  ref: firebase.firestore.DocumentReference;
  type?: string;
  read?: boolean;
  createdAt?: DateValue;
  metadata?: ActivityMetadata & { collectionSnapshot?: { title?: string; senderName?: string } };
}

const getCreatedAt = (notification: CollectionNotification) => {
  const createdAt = notification.createdAt;
  return isTimestamp(createdAt) ? createdAt.toMillis?.() || createdAt.toDate().getTime() || 0 : 0;
};

function CollectionShareModal() {
  const { auth, firestore, firebase } = useFirebase();
  const [user] = useAuthState(auth);
  const { t } = useI18n();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<CollectionNotification[]>([]);
  const [processingId, setProcessingId] = useState("");
  const dismissedNotificationIdsRef = useRef(new Set<string>());

  useEffect(() => {
    dismissedNotificationIdsRef.current.clear();
    if (!user?.uid || !firestore || isDemoUserId(user.uid)) {
      setNotifications([]);
      return undefined;
    }

    return firestore
      .collection("userNotifications")
      .doc(user.uid)
      .collection("notifications")
      .where("read", "==", false)
      .limit(20)
      .onSnapshot(
        (snapshot) => {
          const unreadNotifications = snapshot.docs
              .map((notificationDoc): CollectionNotification => ({
                id: notificationDoc.id,
                ref: notificationDoc.ref,
                ...notificationDoc.data()
              }))
              .filter((notification) =>
                notification.type === "collection_shared" &&
                notification.read !== true &&
                !dismissedNotificationIdsRef.current.has(notification.id)
              )
              .sort((left, right) => getCreatedAt(left) - getCreatedAt(right));
          setNotifications(unreadNotifications);
          if (unreadNotifications.length > 0) {
            markNotificationsLogStale(user.uid, "owner");
          }
        },
        (error) => {
          console.warn("Не удалось загрузить уведомления о подборках:", error);
        }
      );
  }, [firestore, user?.uid]);

  const activeNotification = notifications[0] || null;
  const metadata = activeNotification?.metadata || {};
  const collectionId = String(metadata.sharedCollectionId || activeNotification?.id || "");
  const collectionTitle = metadata.collectionTitle || metadata.collectionSnapshot?.title || t("Личная подборка");
  const senderName = metadata.senderName || metadata.collectionSnapshot?.senderName || t("Пользователь");

  const markAsRead = useCallback(async (notification: CollectionNotification) => {
    if (!notification?.ref || !user?.uid || processingId) return false;
    setProcessingId(notification.id);
    try {
      await notification.ref.set({
        read: true,
        readAt: firebase.firestore.FieldValue.serverTimestamp()
      }, { merge: true });
      markNotificationsLogStale(user.uid, "owner");
      return true;
    } catch (error) {
      console.warn("Не удалось закрыть уведомление о подборке:", error);
      return false;
    } finally {
      setProcessingId("");
    }
  }, [firebase, processingId, user?.uid]);

  const dismissLocally = useCallback((notification: CollectionNotification) => {
    if (!notification?.id) return;
    dismissedNotificationIdsRef.current.add(notification.id);
    setNotifications((currentNotifications) =>
      currentNotifications.filter(({ id }) => id !== notification.id)
    );
  }, []);

  const close = useCallback(() => {
    if (!activeNotification) return;
    dismissLocally(activeNotification);
    void markAsRead(activeNotification);
  }, [activeNotification, dismissLocally, markAsRead]);

  const openCollection = useCallback(() => {
    if (!activeNotification || !user?.uid) return;
    dismissLocally(activeNotification);
    void markAsRead(activeNotification);
    const params = new URLSearchParams({ tab: "personal" });
    if (collectionId) params.set("shared", collectionId);
    navigate(`/${user.uid}/collections?${params.toString()}`);
  }, [activeNotification, collectionId, dismissLocally, markAsRead, navigate, user?.uid]);

  const modal = useMemo(() => {
    if (!activeNotification || isDemoUserId(user?.uid)) return null;
    const disabled = Boolean(processingId);
    return (
      <div className="friend-request-modal collection-share-modal" role="presentation">
        <section
          className="friend-request-modal__card collection-share-modal__card"
          role="dialog"
          aria-modal="true"
          aria-label={t("С вами поделились подборкой")}
        >
          <div className="friend-request-modal__icon" aria-hidden="true">
            <FaListUl />
          </div>
          <button
            className="friend-request-modal__close"
            type="button"
            onClick={close}
            disabled={disabled}
            aria-label={t("Закрыть")}
          >
            <FaTimes />
          </button>
          <div className="collection-share-modal__art" aria-hidden="true">
            <FaListUl />
          </div>
          <p className="friend-request-modal__eyebrow">{t("Новая подборка")}</p>
          <h2>{t("С вами поделились подборкой")}</h2>
          <p className="friend-request-modal__text">
            <strong>{senderName}</strong><br />
            «{collectionTitle}»
          </p>
          <div className="friend-request-modal__actions">
            <button
              className="friend-request-modal__button friend-request-modal__button--secondary"
              type="button"
              onClick={close}
              disabled={disabled}
            >
              {t("Позже")}
            </button>
            <button
              className="friend-request-modal__button friend-request-modal__button--primary"
              type="button"
              onClick={openCollection}
              disabled={disabled}
              autoFocus
            >
              {processingId ? t("Открываем...") : t("Открыть подборку")}
            </button>
          </div>
        </section>
      </div>
    );
  }, [activeNotification, close, collectionTitle, openCollection, processingId, senderName, t, user?.uid]);

  if (!modal || typeof document === "undefined") return null;
  return createPortal(modal, document.body);
}

export default CollectionShareModal;
